import React, { useEffect, useRef, useState } from 'react';

import { fetchAtendimentoExterno, type AtendimentoExternoDetalhe } from '../../../api/oficinaExternaApi';
import { notify } from '../../../shared/feedback/notify';
import { dataCurta, dataHora, diasSemVisitaTexto, valorLegivel } from '../formatacao';
// Overlay e cabeçalho vêm do casco compartilhado de modal (16+ modais usam), e o X vem do
// arquivo único da at-89 — nunca recopiar o bloco da classe.
import '../../../components/modal/add-car/AddCarModal.css';
import '../../../shared/ui/modal-close-button.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../pages/OficinaExterna.css';

interface Props {
  atendimentoId: string;
  placa: string;
  onClose: () => void;
  /** Passar quando a tela tiver sidebar: sem isso o clique fora nunca fecha (lição de 18/08). */
  sidebarRef?: React.RefObject<HTMLElement>;
}

/**
 * O histórico de visitas de um veículo em oficina externa (at-114).
 *
 * Somente leitura, para qualquer papel que o abra: a visita é IMUTÁVEL (ADR 0059) e não existe
 * rota de edição — correção se faz com visita nova, e é por isso que não há botão nenhum aqui.
 */
const AtendimentoExternoModal: React.FC<Props> = ({ atendimentoId, placa, onClose, sidebarRef }) => {
  const [detalhe, setDetalhe] = useState<AtendimentoExternoDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ativo = true;
    void (async () => {
      setCarregando(true);
      try {
        const dados = await fetchAtendimentoExterno(atendimentoId);
        if (!ativo) return;
        setDetalhe(dados);
        setErro(null);
      } catch (error) {
        if (!ativo) return;
        // 🔴 `null`, nunca `{ visitas: [] }`: "nenhuma visita registrada" é afirmação sobre o
        // trabalho do inspetor, e quem a lê depois de uma falha conclui que ninguém foi à oficina.
        setDetalhe(null);
        setErro(
          'Não foi possível carregar as visitas deste veículo. A tela NÃO está dizendo que não ' +
            'há visitas — ela não conseguiu consultar. Feche e abra novamente; se persistir, ' +
            'acione a TI.',
        );
        notify.errorFrom(error, 'Não foi possível carregar as visitas deste veículo.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [atendimentoId]);

  // Fecha por clique fora e por Escape.
  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (modalRef.current?.contains(alvo)) return;
      if (sidebarRef?.current?.contains(alvo)) return;
      onClose();
    };
    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', escape);
    };
  }, [onClose, sidebarRef]);

  return (
    <div className="add-car-modal-overlay">
      {/* `modal-content` é o corpo do modal compartilhado (`AddCarModal.css`); `oe-modal` só
          amplia a largura e dá o scroll do histórico. ⚠️ Não existe classe `add-car-modal` — o
          contrato de classes pegou essa invenção na primeira execução (regra de 16/09/2026). */}
      <div className="modal-content oe-modal" ref={modalRef} role="dialog" aria-modal="true">
        <div className="oe-modal-header">
          <h2>Oficina externa — {placa}</h2>
          <button type="button" className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="oe-modal-body">
          {erro && (
            <div className="erro-de-tela" role="alert">
              {erro}
            </div>
          )}

          {carregando && <p>Carregando...</p>}

          {!carregando && detalhe && (
            <>
              <section className="oe-bloco">
                <h3>Veículo</h3>
                <dl className="oe-dados">
                  <div>
                    <dt>Oficina</dt>
                    <dd>
                      {detalhe.oficina?.nome} ({detalhe.oficina?.regiao?.nome})
                    </dd>
                  </div>
                  <div>
                    <dt>Evento</dt>
                    <dd>{detalhe.evento?.eventNumber ?? '-'}</dd>
                  </div>
                  <div>
                    <dt>Modelo</dt>
                    <dd>{detalhe.modelo || '-'}</dd>
                  </div>
                  <div>
                    <dt>Entrada</dt>
                    <dd>{dataCurta(detalhe.entradaEm)}</dd>
                  </div>
                  <div>
                    <dt>Saída</dt>
                    <dd>{detalhe.saidaEm ? dataCurta(detalhe.saidaEm) : 'Ainda na oficina'}</dd>
                  </div>
                  <div>
                    <dt>Etapa informada</dt>
                    <dd>{detalhe.etapaAtual?.nome || 'Nenhuma informada'}</dd>
                  </div>
                  <div>
                    <dt>Previsão da etapa</dt>
                    <dd>{dataCurta(detalhe.previsaoEtapa)}</dd>
                  </div>
                  <div>
                    <dt>Previsão de entrega total</dt>
                    <dd>{dataCurta(detalhe.previsaoEntrega)}</dd>
                  </div>
                  <div>
                    <dt>Última visita</dt>
                    <dd>{diasSemVisitaTexto(detalhe.diasSemVisita)}</dd>
                  </div>
                </dl>
                {detalhe.saidaMotivo && (
                  <p className="oe-hint">
                    <strong>Motivo da saída:</strong> {detalhe.saidaMotivo}
                  </p>
                )}
              </section>

              <section className="oe-bloco">
                <h3>Visitas ({detalhe.visitas.length})</h3>

                {detalhe.visitas.length === 0 && (
                  <p className="oe-vazio">
                    Nenhuma visita registrada neste veículo até agora.
                  </p>
                )}

                {detalhe.visitas.map((visita) => (
                  <article key={visita.id} className="oe-visita">
                    <header className="oe-visita-header">
                      <strong>{dataCurta(visita.dataVisita)}</strong>
                      <span>{visita.inspetor?.fullName || visita.inspetor?.username || '-'}</span>
                      {/* As duas datas só aparecem juntas quando DIVERGEM: o inspetor trabalha
                          sem rede, e o registro pode chegar dias depois da visita. */}
                      {dataCurta(visita.dataVisita) !== dataCurta(visita.registradaEm) && (
                        <span
                          className="oe-visita-registro"
                          title="A visita foi feita nesta data e o registro chegou ao sistema depois — o inspetor estava sem rede."
                        >
                          registrada em {dataHora(visita.registradaEm)}
                        </span>
                      )}
                    </header>

                    <div className="oe-visita-corpo">
                      {visita.etapa && (
                        <p>
                          <strong>Etapa informada:</strong> {visita.etapa.nome}
                        </p>
                      )}
                      {visita.previsaoEtapa && (
                        <p>
                          <strong>Previsão da etapa:</strong> {dataCurta(visita.previsaoEtapa)}
                        </p>
                      )}
                      {visita.previsaoEntrega && (
                        <p>
                          <strong>Previsão de entrega total:</strong>{' '}
                          {dataCurta(visita.previsaoEntrega)}
                        </p>
                      )}
                      {visita.observacao && <p className="oe-observacao">{visita.observacao}</p>}

                      {visita.respostas.length > 0 && (
                        <ul className="oe-respostas">
                          {visita.respostas.map((r) => (
                            <li key={r.itemId}>
                              <span className="oe-resposta-rotulo">
                                {r.rotulo || 'Item removido do checklist'}
                                {r.itemAtivo === false && (
                                  <em
                                    className="oe-resposta-inativo"
                                    title="Este item não faz mais parte do checklist, mas a resposta dada na época continua registrada."
                                  >
                                    {' '}
                                    (item desativado)
                                  </em>
                                )}
                              </span>
                              <span>{valorLegivel(r)}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {visita.fotos.length > 0 && (
                        <div className="oe-fotos">
                          {visita.fotos.map((foto) =>
                            // at-132: vídeo e foto vêm na mesma lista; o `mime` diz qual é.
                            (foto.mime || '').startsWith('video/') ? (
                              <video
                                key={foto.id}
                                src={foto.url}
                                controls
                                preload="metadata"
                                playsInline
                                className="oe-video"
                                aria-label="Vídeo da visita"
                              />
                            ) : (
                              <a
                                key={foto.id}
                                href={foto.url}
                                target="_blank"
                                rel="noreferrer"
                                title="Abrir a foto em nova aba"
                              >
                                <img src={foto.url} alt="Foto da visita" loading="lazy" />
                              </a>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AtendimentoExternoModal;
